import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const LEADERBOARD_DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

interface DurationSelectProps {
  value: number;
  onChange: (duration: number) => void;
}

function DurationSelect({ value, onChange }: DurationSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative w-[4.75rem] shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('leaderboard.minDuration')}
        className="flex w-full items-center justify-between gap-1 rounded-lg border border-room-border bg-room-surface-3 py-1.5 pl-2.5 pr-2 text-xs font-medium tabular-nums text-room-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent"
      >
        <span className="truncate">{t('leaderboard.minDurationChip', { duration: value })}</span>
        <ChevronDown
          size={12}
          className={`shrink-0 text-room-text-faint transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label={t('leaderboard.minDuration')}
          className="absolute right-0 top-[calc(100%+4px)] z-40 w-full overflow-hidden rounded-lg border border-room-border bg-room-surface-2 py-1 shadow-lg"
        >
          {LEADERBOARD_DURATION_OPTIONS.map(d => {
            const selected = d === value;
            return (
              <button
                key={d}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(d);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-2.5 py-1.5 text-xs font-medium tabular-nums transition-colors hover:bg-room-surface-3 ${
                  selected ? 'text-room-accent' : 'text-room-text-dim'
                }`}
              >
                <span>{t('leaderboard.minDurationChip', { duration: d })}</span>
                <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                  {selected ? <Check size={12} strokeWidth={2.5} /> : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface LeaderboardFiltersProps {
  minDuration: number;
  onMinDurationChange: (duration: number) => void;
}

export function LeaderboardFilters({ minDuration, onMinDurationChange }: LeaderboardFiltersProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={`${t('leaderboard.filters')}: ${t('leaderboard.minDurationChip', { duration: minDuration })}`}
        className={`flex h-[38px] w-[38px] items-center justify-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent ${
          open
            ? 'bg-room-surface-3 text-room-text'
            : 'text-room-text-dim hover:bg-room-surface-3 hover:text-room-text'
        }`}
      >
        <SlidersHorizontal size={16} />
      </button>
      {open ? (
        <div className="absolute right-0 top-[calc(100%+6px)] z-30 rounded-xl border border-room-border bg-room-surface-2 p-2.5 shadow-lg">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs font-medium text-room-text-dim">
              {t('leaderboard.durationLabel')}
            </span>
            <DurationSelect value={minDuration} onChange={onMinDurationChange} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
